import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FiCheck, FiChevronLeft, FiFileText, FiPaperclip, FiTrash2 } from 'react-icons/fi';
import { ROUTES } from '../../../app/routes';
import { useAuth } from '../../auth/AuthContext';
import AqSelect from './AqSelect';
import { OPPS, type Opportunity, type Submission, type SubDoc } from './submissionData';
import { todayISO, type DocType } from './QuoteFieldsPanel';
import {
  getNcToken, getToolboxToken, parseAmount, toDateInput, type SelectedOption,
} from '../../acordsmartscan/acordSmartScanShared';
import { searchClients, getClientPolicies, type ClientSearchResult } from '../../../services/quoteProposalService';
import '../../commissionscan/CommissionScan.css';
import '../../../styles/SubmissionCenter.css';
import '../../../styles/AddQuotePage.css';
import '../../../styles/AddOpportunityPage.css';
import { createProspect, createOpportunity, createQuote, fetchLinesOfBusiness, fetchOpportunityStages, fileToBase64, BUSINESS_TYPE_VALUES, oneYearLater } from './submissionApi';

// SubDoc plus the real File it came from — kept alongside the display metadata
// so the first attachment can be uploaded (via create-quote's chained call)
// once the quote it belongs to actually exists.
type QuoteDoc = SubDoc & { file?: File };

const MYAMP_BASE = process.env.REACT_APP_MyAMP_Url;

const STEPS = ['client', 'details', 'quote'] as const;
type Step = typeof STEPS[number];
const STEP_LABELS: Record<Step, string> = { client: 'Client', details: 'Details', quote: 'Quote' };
const TYPE_OPTIONS = ['New Business', 'Renewal'];
// NowCerts' opportunity stages and win-probability tags are agency-configurable, not a
// fixed global enum — these are just suggestions (via datalist), free text is accepted.
const WIN_PROBABILITY_OPTIONS = ['Excellent', 'Very Good', 'Good', 'Moderate', 'Not Likely'];
interface AccountOption {
    account: string;
    dba?: string | null;
    contact: Opportunity['contact'];
    insuredId?: string;
}
const deriveLobShort = (lobs: string[]): string => {
    const first = lobs[0] || '';
    const words = first.split(/\s+/).filter(Boolean);
    if (words.length >= 2)
        return words.map(w => w[0]).join('').toUpperCase().slice(0, 3);
    return first.slice(0, 3).toUpperCase();
};
const AddOpportunityPage: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [step, setStep] = useState<Step>('client');
    const [maxStep, setMaxStep] = useState(0);
    const [clientMode, setClientMode] = useState<'existing' | 'new'>('existing');
    const [clientQuery, setClientQuery] = useState('');
    const [selectedAccount, setSelectedAccount] = useState<AccountOption | null>(null);
    // Tracked separately from selectedAccount's nullability so the "hide the
    // results dropdown" check below doesn't narrow selectedAccount's type in
    // the JSX that follows (TS's const-narrowing otherwise carries a
    // `!selectedAccount` check into the nested .map() closure and mistypes
    // the later `selectedAccount?.insuredId` read there as `never`).
    const [hasPickedAccount, setHasPickedAccount] = useState(false);
    const [newBusinessName, setNewBusinessName] = useState('');
    const [newAddressLine1, setNewAddressLine1] = useState('');
    const [newCity, setNewCity] = useState('');
    const [newState, setNewState] = useState('');
    const [newZipCode, setNewZipCode] = useState('');
    const [newContactEmail, setNewContactEmail] = useState('');
    const [newContactPhone, setNewContactPhone] = useState('');
    const [creatingInsured, setCreatingInsured] = useState(false);
    const [createInsuredError, setCreateInsuredError] = useState('');
    const [searchResults, setSearchResults] = useState<ClientSearchResult[]>([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => {
        const q = clientQuery.trim();
        if (searchDebounceRef.current)
            clearTimeout(searchDebounceRef.current);
        if (!q) {
            setSearchResults([]);
            setSearchLoading(false);
            return;
        }
        setSearchLoading(true);
        searchDebounceRef.current = setTimeout(async () => {
            try {
                const results = await searchClients(q);
                setSearchResults(results);
            }
            catch {
                setSearchResults([]);
            }
            finally {
                setSearchLoading(false);
            }
        }, 400);
        return () => {
            if (searchDebounceRef.current)
                clearTimeout(searchDebounceRef.current);
        };
    }, [clientQuery]);
    const loadInsuredDetails = useCallback(async (insuredId: string) => {
        if (!insuredId)
            return;
        try {
            await getClientPolicies(insuredId);
        }
        catch {
        }
    }, []);
    const selectAccount = (client: ClientSearchResult) => {
        setSelectedAccount({
            account: client.name,
            dba: null,
            contact: { name: client.name, title: '—', email: client.eMail || '—', phone: '—' },
            insuredId: client.databaseId,
        });
        setSearchResults([]);
        setHasPickedAccount(true);
        void loadInsuredDetails(client.databaseId);
    };
    const step1Valid = clientMode === 'existing'
        ? !!selectedAccount
        : !!newBusinessName.trim();
    // Hides the results/"No matches" dropdown once an account is picked — the
    // query text stays in the box (so the field still shows what was searched)
    // but there's nothing left to pick from, and no match to complain about.
    const showClientResults = !!clientQuery.trim() && !hasPickedAccount;
    const [oppName, setOppName] = useState('');
    const [lob, setLob] = useState('');
    const [lobOptions, setLobOptions] = useState<string[]>([]);
    const [lobQuery, setLobQuery] = useState('');
    const lobQueryTouchedRef = useRef(false);
    const lobSearchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => {
        let cancelled = false;
        fetchLinesOfBusiness()
            .then((list) => { if (!cancelled) setLobOptions(list); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, []);
    // Search-as-you-type — re-queries NowCerts itself (odata $filter=name eq
    // '<query>') via fetchLinesOfBusiness's `name` param instead of filtering
    // the list already in state, same as Submission Center's Configuration tab.
    useEffect(() => {
        if (!lobQueryTouchedRef.current) return;
        if (lobSearchDebounceRef.current) clearTimeout(lobSearchDebounceRef.current);
        let cancelled = false;
        lobSearchDebounceRef.current = setTimeout(() => {
            fetchLinesOfBusiness(lobQuery)
                .then((list) => { if (!cancelled) setLobOptions(list); })
                .catch(() => { if (!cancelled) setLobOptions([]); });
        }, 400);
        return () => {
            cancelled = true;
            if (lobSearchDebounceRef.current) clearTimeout(lobSearchDebounceRef.current);
        };
    }, [lobQuery]);
    const [winProbability, setWinProbability] = useState('');
    const [type, setType] = useState('New Business');
    const [stage, setStage] = useState('');
    const [stageOptions, setStageOptions] = useState<string[]>([]);
    useEffect(() => {
        let cancelled = false;
        fetchOpportunityStages()
            .then((list) => { if (!cancelled) setStageOptions(list); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, []);
    const [effectiveDate, setEffectiveDate] = useState('');
    const [expiringDate, setExpiringDate] = useState('');
    const [expiringPremium, setExpiringPremium] = useState('');
    const [agencyCommission, setAgencyCommission] = useState('');
    const [oppNote, setOppNote] = useState('');
    const [createdProspectId, setCreatedProspectId] = useState('');
    const [creatingOpportunity, setCreatingOpportunity] = useState(false);
    const [createOpportunityError, setCreateOpportunityError] = useState('');
    const step2Valid = !!(oppName.trim() && lob.trim() && type && stage.trim() &&
        winProbability.trim() && agencyCommission.trim() && effectiveDate);
    const [createdOpportunityId, setCreatedOpportunityId] = useState('');

    // Step 3 — quote details use the minimal set of fields Zapier/InsertQuote actually
    // accepts; scan-to-prefill, Note, and Documents are copied verbatim from
    // QuoteFieldsPanel (not the shared component itself, so AddQuotePage.tsx is unaffected).
    const [quoteNumber, setQuoteNumber] = useState('');
    const [quoteEffectiveDate, setQuoteEffectiveDate] = useState('');
    const [quoteExpirationDate, setQuoteExpirationDate] = useState('');
    const [quoteCarrierName, setQuoteCarrierName] = useState('');
    const [quoteBusinessType, setQuoteBusinessType] = useState('New Business');
    const [quotePremium, setQuotePremium] = useState('');
    const [quoteNote, setQuoteNote] = useState('');
    const [quoteDocs, setQuoteDocs] = useState<QuoteDoc[]>([]);
    const [trackingTransactionId, setTrackingTransactionId] = useState('');
    const quoteValid = !!(quoteNumber.trim() && quoteEffectiveDate && quoteCarrierName.trim() && quotePremium.trim());
    // Picking an effective date defaults expiration to one year later — the user can
    // still edit expiration afterward, this only fires on the effective date's own change.
    const handleQuoteEffectiveDateChange = (value: string) => {
        setQuoteEffectiveDate(value);
        if (value) setQuoteExpirationDate(oneYearLater(value));
    };

    const [documentType, setDocumentType] = useState<DocType>('DecPage');
    const [scanFile, setScanFile] = useState<File | null>(null);
    const [scanDragging, setScanDragging] = useState(false);
    const [scanLoading, setScanLoading] = useState(false);
    const [scanError, setScanError] = useState('');
    const scanInputRef = useRef<HTMLInputElement>(null);
    const [attachDragging, setAttachDragging] = useState(false);
    const attachInputRef = useRef<HTMLInputElement>(null);

    const onScanDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setScanDragging(false);
        const f = e.dataTransfer.files?.[0];
        if (f) setScanFile(f);
    };
    const onScanFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0];
        if (f) setScanFile(f);
        e.target.value = '';
    };

    const onScanAndPrefill = useCallback(async () => {
        if (!scanFile) return;
        setScanLoading(true);
        setScanError('');

        // opportunity-tracking-submission scans the dropped dec page/quote letter and
        // returns { transactionId, extracted: {...} } — extracted fields prefill the
        // Quote Details fields below, replacing the disabled accord_scan flow's job.
        try {
            const insuredId = clientMode === 'existing' ? (selectedAccount?.insuredId || '') : createdProspectId;
            const trackingData = new FormData();
            trackingData.append('file', scanFile);
            trackingData.append('insuredId', insuredId);
            trackingData.append('opportunityId', createdOpportunityId);
            const trackingRes = await axios.post(
                `${MYAMP_BASE}/api/ams/opportunity-tracking-submission`,
                trackingData,
                { headers: { Authorization: `Bearer ${localStorage.getItem('authToken') || ''}` } },
            );
            setTrackingTransactionId(trackingRes.data?.transactionId || '');
            const extracted = trackingRes.data?.extractedJSON;
            if (extracted) {
                if (extracted.carrierName) setQuoteCarrierName(extracted.carrierName);
                if (extracted.premium) setQuotePremium(String(parseAmount(extracted.premium)));
                if (extracted.effectiveDate) setQuoteEffectiveDate(toDateInput(extracted.effectiveDate));
                if (extracted.expirationDate) setQuoteExpirationDate(toDateInput(extracted.expirationDate));
                if (extracted.quoteNumber) setQuoteNumber(extracted.quoteNumber);
                if (extracted.businessType && extracted.businessType in BUSINESS_TYPE_VALUES) {
                    setQuoteBusinessType(extracted.businessType);
                }
                setScanFile(null);
            }
        }
        catch (trackErr) {
            console.log('opportunity-tracking-submission error:', trackErr);
            setScanError('Could not extract details from this document.');
        }
        finally {
            setScanLoading(false);
        }

        // accord_scan (https://acordllmextractor-stage.insurancegig.com/accord_scan) is
        // disabled for now — do not call this API. Left in place, commented out, so the
        // scan-and-prefill behavior is easy to restore later.
        // try {
        //     const data = new FormData();
        //     data.append('File', scanFile);
        //     data.append('documentType', documentType);
        //     data.append('ncToken', getNcToken());
        //     data.append('folderId', '');
        //     data.append('insuredId', '');
        //     data.append('isFileUploadFromAMS', 'false');
        //     data.append('fileName', scanFile.name);
        //     const selectedOption: SelectedOption = {
        //         SelectProspect: false, SelectInsured: false, CreateNewClient: false,
        //         UseExistingInsured: false, UseExistingProspect: false, CreateQuote: false,
        //         CreateNewPolicy: false, CreateNewProspect: false, ClientName: '',
        //         InsuredId: '', folderId: '', fileIds: [], isFileUploadFromAMSParam: false,
        //     };
        //     data.append('selectedOption', JSON.stringify(selectedOption));
        //
        //     const res = await axios.post(`${process.env.REACT_APP_AcordScan_Url}/accord_scan`, data, {
        //         headers: { Authorization: `Bearer ${getToolboxToken()}` },
        //     });
        //
        //     if (res.data.success === true) {
        //         const raw = typeof res.data.acordFormResponse === 'string'
        //             ? JSON.parse(res.data.acordFormResponse)
        //             : (res.data.acordFormResponse || res.data);
        //
        //         if (raw.carrierName) setQuoteCarrierName(raw.carrierName);
        //         if (raw.policyPremium) setQuotePremium(String(parseAmount(raw.policyPremium)));
        //         if (raw.effectiveDate) setQuoteEffectiveDate(toDateInput(raw.effectiveDate));
        //         if (raw.expirationDate) setQuoteExpirationDate(toDateInput(raw.expirationDate));
        //         const idFromScan = raw.quoteNumber || raw.policyNumber;
        //         if (idFromScan) setQuoteNumber(idFromScan);
        //
        //         setQuoteDocs(prev => [...prev, {
        //             name: scanFile.name,
        //             kind: documentType === 'DecPage' ? 'Dec Page' : 'Quote Letter',
        //             ok: true,
        //             note: 'Auto-attached from scan',
        //             when: todayISO(),
        //         }]);
        //         setScanFile(null);
        //     }
        //     else {
        //         setScanError(res.data.errorMessage || 'Could not extract details from this document.');
        //     }
        // }
        // catch (err: any) {
        //     setScanError(err?.response?.data?.errorMessage || 'Scan failed — check the file and try again.');
        // }
        // finally {
        //     setScanLoading(false);
        // }
    }, [scanFile, documentType, clientMode, selectedAccount, createdProspectId, createdOpportunityId]);

    const addDocsFromFiles = (files: File[]) => {
        if (!files.length) return;
        setQuoteDocs(prev => [...prev, ...files.map(f => ({
            name: f.name, kind: 'Attachment', ok: true, note: 'Attached manually', when: todayISO(), file: f,
        }))]);
    };
    const onAttachDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setAttachDragging(false);
        addDocsFromFiles(Array.from(e.dataTransfer.files || []));
    };
    const onAttachFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        addDocsFromFiles(Array.from(e.target.files || []));
        e.target.value = '';
    };
    const removeQuoteDoc = (idx: number) => setQuoteDocs(prev => prev.filter((_, i) => i !== idx));

    const [creatingQuote, setCreatingQuote] = useState(false);
    const [createQuoteError, setCreateQuoteError] = useState('');
    const goTo = (s: Step, idx: number) => { setStep(s); setMaxStep(m => Math.max(m, idx)); };
    const handleContinueStep1 = async () => {
        if (clientMode !== 'new') {
            goTo('details', 1);
            return;
        }
        setCreateInsuredError('');
        setCreatingInsured(true);
        try {
            const data = await createProspect({
                commercialName: newBusinessName.trim(),
                addressLine1: newAddressLine1.trim() || undefined,
                city: newCity.trim() || undefined,
                state: newState.trim() || undefined,
                zipCode: newZipCode.trim() || undefined,
                email: newContactEmail.trim() || undefined,
                phone: newContactPhone.trim() || undefined,
            });
            // Zapier/InsertInsured returns the new record's id as database_id (snake_case).
            const newId = data?.response?.database_id || '';
            setCreatedProspectId(newId);
            goTo('details', 1);
        }
        catch {
            setCreateInsuredError('Failed to create the prospect. Please try again.');
        }
        finally {
            setCreatingInsured(false);
        }
    };
    const resolvedInsuredDatabaseId = clientMode === 'existing' ? (selectedAccount?.insuredId || '') : createdProspectId;
    const handleContinueStep2 = async () => {
        // Guards against a duplicate opportunity if this ever gets triggered a second
        // time after the first call already succeeded (belt-and-suspenders alongside
        // locking the step tabs/removing Back once createdOpportunityId is set).
        if (createdOpportunityId) {
            goTo('quote', 2);
            return;
        }
        setCreateOpportunityError('');
        setCreatingOpportunity(true);
        try {
            const insuredCommercialName = clientMode === 'existing' ? (selectedAccount?.account || '') : newBusinessName.trim();
            const insuredEmailRaw = clientMode === 'existing' ? selectedAccount?.contact.email : newContactEmail.trim();
            const insuredEmail = insuredEmailRaw && insuredEmailRaw !== '—' ? insuredEmailRaw : undefined;
            const data = await createOpportunity({
                lineOfBusinessName: lob.trim(),
                neededBy: effectiveDate || undefined,
                opportunityStageName: stage.trim(),
                winProbability: winProbability.trim() || undefined,
                assignedTo: user?.name ? [user.name] : (user?.email ? [user.email] : undefined),
                description: oppNote.trim() || undefined,
                agencyCommission: agencyCommission.trim() ? Number(agencyCommission) : undefined,
                insuredDatabaseId: resolvedInsuredDatabaseId || undefined,
                insuredEmail,
                insuredCommercialName: insuredCommercialName || undefined,
                createdFromRenewal: type === 'Renewal',
            });
            // Unlike Zapier/InsertInsured's snake_case database_id, InsertOpportunity's
            // response uses camelCase databaseId.
            setCreatedOpportunityId(data?.response?.databaseId || '');
            goTo('quote', 2);
        }
        catch {
            setCreateOpportunityError('Failed to create the opportunity. Please try again.');
        }
        finally {
            setCreatingOpportunity(false);
        }
    };

    // Links a scanned dec page/quote letter's tracking row (opportunity-tracking-submission)
    // to the quote NowCerts just created for it. Best-effort by design: called right after
    // createQuote succeeds, but a failure here never blocks the wizard from finishing since
    // the quote itself already exists by that point.
    const updateTrackingSubmissionQuoteId = async (transactionId: string, quoteId: string) => {
        try {
            await axios.patch(
                `${MYAMP_BASE}/api/ams/opportunity-tracking-submission/update`,
                { transactionId, quoteId },
                { headers: { Authorization: `Bearer ${localStorage.getItem('authToken') || ''}` } },
            );
        } catch (updateErr) {
            console.log('opportunity-tracking-submission/update error:', updateErr);
        }
    };

    const finish = async (withQuote: boolean) => {
        if (withQuote && quoteValid) {
            setCreateQuoteError('');
            setCreatingQuote(true);
            try {
                // Only the first attachment travels with the quote — NowCerts'
                // UploadPolicyFile takes one file per call, and this is chained
                // as a single best-effort call the same way Note is.
                const firstDoc = quoteDocs.find(d => d.file);
                const fileContentBase64 = firstDoc?.file ? await fileToBase64(firstDoc.file) : undefined;
                const quoteRes = await createQuote({
                    number: quoteNumber.trim(),
                    effectiveDate: quoteEffectiveDate || undefined,
                    expirationDate: quoteExpirationDate || undefined,
                    carrierName: quoteCarrierName.trim() || undefined,
                    businessType: BUSINESS_TYPE_VALUES[quoteBusinessType] ?? 0,
                    premium: quotePremium ? Number(quotePremium) : undefined,
                    insuredDatabaseId: resolvedInsuredDatabaseId || undefined,
                    opportunityIds: createdOpportunityId ? [createdOpportunityId] : undefined,
                    note: quoteNote.trim() || undefined,
                    fileName: firstDoc?.file?.name,
                    fileContentBase64,
                });

                // Only relevant when Step 3 was prefilled from a scanned dec page/quote letter.
                if (trackingTransactionId) {
                    await updateTrackingSubmissionQuoteId(trackingTransactionId, quoteRes?.response?.databaseId || '');
                }
            }
            catch {
                setCreateQuoteError('Failed to create the quote. Please try again.');
                setCreatingQuote(false);
                return;
            }
            setCreatingQuote(false);
        }
        const account = clientMode === 'existing' ? selectedAccount?.account : newBusinessName.trim();
        if (!account)
            return;
        const dba = clientMode === 'existing' ? (selectedAccount?.dba ?? null) : null;
        const contact = clientMode === 'existing' && selectedAccount
            ? selectedAccount.contact
            : {
                name: newBusinessName.trim(), title: '—',
                email: newContactEmail.trim() || '—', phone: newContactPhone.trim() || '—',
            };
        const lobList = lob.trim() ? [lob.trim()] : ['General Liability'];
        const newOpp: Opportunity = {
            id: `opp-${Date.now()}`,
            account, dba,
            oppName: oppName.trim(),
            lobs: lobList,
            lobShort: deriveLobShort(lobList),
            type, oppStage: 'marketing', tier: 'Level 2',
            effective: effectiveDate,
            expiring: type === 'Renewal' ? (expiringDate || null) : null,
            expiringPremium: type === 'Renewal' && expiringPremium ? Number(expiringPremium) : null,
            targetPremium: 0,
            producer: user?.name || '—', csr: '—', marketer: '—',
            contact,
            quoteRef: `QT-NEW-${Date.now().toString().slice(-4)}`,
            health: 'on',
            tracker: { shared: false, views: 0, lastView: todayISO() },
            nextAction: { label: 'Kick off marketing to carriers', owner: user?.name || '—', due: todayISO() },
            note: oppNote.trim(),
            submissions: [],
            clientDocs: [], clientMsgs: [], automations: [],
        };
        if (withQuote && quoteValid) {
            const newSub: Submission = {
                id: `sub-${Date.now()}`,
                carrier: quoteCarrierName.trim(),
                market: 'Admitted',
                channel: '—',
                submissionNo: quoteNumber.trim(),
                lob: lobList[0],
                stage: 'quoted',
                submittedOn: todayISO(),
                lastActivity: todayISO(),
                slaDays: 5,
                premium: quotePremium ? Number(quotePremium) : null,
                commission: null,
                limits: '—',
                deductible: '—',
                validUntil: null,
                effectiveDate: quoteEffectiveDate || null,
                expDate: quoteExpirationDate || null,
                note: quoteNote.trim() || undefined,
                uw: { name: 'Not yet assigned', email: '—', phone: '—' },
                subjectivities: [],
                flags: [],
                docs: quoteDocs,
                comms: [],
            };
            newOpp.submissions.push(newSub);
        }
        OPPS.push(newOpp);
        navigate(ROUTES.CENTER_SUBMISSION_TRACKER);
    };
    return (<div className="aq-page">
      <div className="aq-page__inner">
        <button type="button" className="cs-back-link" onClick={() => navigate(ROUTES.CENTER_SUBMISSION_TRACKER)}>
          <FiChevronLeft size={13}/> Back to Submission Center
        </button>

        <header className="aq-head">
          <h1 className="aq-head__title">Add an opportunity</h1>
          <p className="aq-head__blurb">
            Find or create the client, capture the opportunity, and optionally log its first quote.
          </p>
        </header>

        <div className="aq-steps">
          {STEPS.map((s, i) => {
            // Once the opportunity is created, Client/Details are locked — going back and
            // hitting Continue again would create a duplicate prospect/opportunity.
            const lockedByCreation = !!createdOpportunityId && s !== 'quote';
            const isComplete = i < STEPS.indexOf(step);
            return (<button key={s} type="button" className={`aq-step${step === s ? ' aq-step--active' : ''}${isComplete ? ' aq-step--complete' : ''}`} disabled={i > maxStep || lockedByCreation} onClick={() => setStep(s)}>
              <span className="aq-step__num">{isComplete ? <FiCheck size={11} /> : i + 1}</span>
              {STEP_LABELS[s]}
            </button>);
          })}
        </div>

        {step === 'client' && (<>
            <section className="sc-panel__section aq-section">
              <h3 className="sc-panel__section-title">Client or prospect</h3>
              <div className="cs-tabs">
                <button type="button" className={`cs-tab${clientMode === 'existing' ? ' cs-tab--active' : ''}`} onClick={() => setClientMode('existing')}>
                  Existing account
                </button>
                <button type="button" className={`cs-tab${clientMode === 'new' ? ' cs-tab--active' : ''}`} onClick={() => setClientMode('new')}>
                  New prospect
                </button>
              </div>

              {clientMode === 'existing' ? (<div className="aq-client-search">
                  <div className="aq-client-search__input-wrap">
                    <input className="glass-input" value={clientQuery} onChange={(e) => {
                    setClientQuery(e.target.value);
                    setSelectedAccount(null);
                    setHasPickedAccount(false);
                }} placeholder="Search accounts by name…"/>
                    {searchLoading && <span className="aq-client-search__spinner" aria-hidden="true"/>}
                  </div>
                  {showClientResults && (<ul className="aq-client-results">
                      {searchLoading ? (<li className="aq-client-empty">Searching…</li>) : searchResults.length === 0 ? (<li className="aq-client-empty">No matches — switch to "New prospect" to add them.</li>) : searchResults.map(c => (<li key={c.databaseId}>
                          <button type="button" className={`aq-client-result${selectedAccount?.insuredId === c.databaseId ? ' aq-client-result--picked' : ''}`} onClick={() => selectAccount(c)}>
                            <span className="aq-client-result__name">{c.name}</span>
                            {c.eMail && <span className="aq-client-result__meta">{c.eMail}</span>}
                          </button>
                        </li>))}
                    </ul>)}
                  {selectedAccount && (<p className="aq-client-picked">Selected: <strong>{selectedAccount.account}</strong></p>)}
                </div>) : (<div className="aq-grid">
                  <div className="aq-field">
                    <label className="aq-label">Business / prospect name *</label>
                    <input className="glass-input" value={newBusinessName} onChange={(e) => setNewBusinessName(e.target.value)} placeholder="e.g. Harbor Fitness LLC"/>
                  </div>
                  <div className="aq-field">
                    <label className="aq-label">Address line 1</label>
                    <input className="glass-input" value={newAddressLine1} onChange={(e) => setNewAddressLine1(e.target.value)} placeholder="e.g. 100 Main St"/>
                  </div>
                  <div className="aq-field">
                    <label className="aq-label">City</label>
                    <input className="glass-input" value={newCity} onChange={(e) => setNewCity(e.target.value)} placeholder="e.g. Austin"/>
                  </div>
                  <div className="aq-field">
                    <label className="aq-label">State</label>
                    <input className="glass-input" value={newState} onChange={(e) => setNewState(e.target.value)} placeholder="e.g. TX"/>
                  </div>
                  <div className="aq-field">
                    <label className="aq-label">Zip code</label>
                    <input className="glass-input" value={newZipCode} onChange={(e) => setNewZipCode(e.target.value)} placeholder="e.g. 78701"/>
                  </div>
                  <div className="aq-field">
                    <label className="aq-label">Email</label>
                    <input type="email" className="glass-input" value={newContactEmail} onChange={(e) => setNewContactEmail(e.target.value)} placeholder="e.g. jamie@harborfitness.com"/>
                  </div>
                  <div className="aq-field">
                    <label className="aq-label">Phone</label>
                    <input className="glass-input" value={newContactPhone} onChange={(e) => setNewContactPhone(e.target.value)} placeholder="e.g. (555) 200-1000"/>
                  </div>
                </div>)}
              {createInsuredError && <p className="aq-note" style={{ color: '#f87171' }}>{createInsuredError}</p>}
            </section>

            <div className="aq-submitbar">
              <button type="button" className="glass-btn aq-cancel" onClick={() => navigate(ROUTES.CENTER_SUBMISSION_TRACKER)}>Cancel</button>
              <button type="button" className="cs-browse-btn" disabled={!step1Valid || creatingInsured} onClick={handleContinueStep1}>
                {creatingInsured ? 'Creating…' : 'Continue'}
              </button>
            </div>
          </>)}

        {step === 'details' && (<>
            <section className="sc-panel__section aq-section">
              <h3 className="sc-panel__section-title">Opportunity details</h3>
              <div className="aq-grid">
                <div className="aq-field">
                  <label className="aq-label">Opportunity name *</label>
                  <input className="glass-input" value={oppName} onChange={(e) => setOppName(e.target.value)} placeholder="e.g. GL — New Business"/>
                </div>
                <div className="aq-field">
                  <label className="aq-label">Type *</label>
                  <AqSelect value={type} onChange={setType} options={TYPE_OPTIONS} />
                </div>
                <div className="aq-field">
                  <label className="aq-label">Line of business *</label>
                  <AqSelect
                    value={lob}
                    onChange={setLob}
                    options={lobOptions}
                    searchable
                    onSearchChange={(q) => { lobQueryTouchedRef.current = true; setLobQuery(q); }}
                  />
                </div>
                <div className="aq-field">
                  <label className="aq-label">Win probability *</label>
                  <AqSelect value={winProbability} onChange={setWinProbability} options={WIN_PROBABILITY_OPTIONS} />
                </div>
                <div className="aq-field">
                  <label className="aq-label">Stage *</label>
                  <AqSelect value={stage} onChange={setStage} options={stageOptions} searchable />
                </div>
                <div className="aq-field">
                  <label className="aq-label">Agency commission *</label>
                  <input type="number" min="0" step="0.01" className="glass-input" value={agencyCommission} onChange={(e) => setAgencyCommission(e.target.value)} placeholder="e.g. 12.5"/>
                </div>
                <div className="aq-field">
                  <label className="aq-label">Effective date *</label>
                  <input type="date" className="glass-input" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)}/>
                </div>
                {type === 'Renewal' && (<>
                    <div className="aq-field">
                      <label className="aq-label">Expiring date</label>
                      <input type="date" className="glass-input" value={expiringDate} onChange={(e) => setExpiringDate(e.target.value)}/>
                    </div>
                    <div className="aq-field">
                      <label className="aq-label">Expiring premium</label>
                      <input type="number" min="0" className="glass-input" value={expiringPremium} onChange={(e) => setExpiringPremium(e.target.value)} placeholder="e.g. 184200"/>
                    </div>
                  </>)}
              </div>

            </section>

            <section className="sc-panel__section aq-section">
              <h3 className="sc-panel__section-title">Note</h3>
              <textarea className="glass-input aq-note" rows={3} value={oppNote} onChange={(e) => setOppNote(e.target.value)} placeholder="Add a note about this opportunity…"/>
            </section>

            {createOpportunityError && <p className="aq-note" style={{ color: '#f87171' }}>{createOpportunityError}</p>}

            <div className="aq-submitbar">
              <button type="button" className="glass-btn aq-cancel" onClick={() => goTo('client', 0)}>Back</button>
              <button type="button" className="cs-browse-btn" disabled={!step2Valid || creatingOpportunity} onClick={handleContinueStep2}>
                {creatingOpportunity ? 'Creating…' : 'Continue'}
              </button>
            </div>
          </>)}

        {step === 'quote' && (<>
            <section className="sc-panel__section aq-section">
              <h3 className="sc-panel__section-title">Scan a dec page or quote letter to prefill</h3>
              <div className="cs-tabs">
                <button
                  type="button"
                  className={`cs-tab${documentType === 'DecPage' ? ' cs-tab--active' : ''}`}
                  onClick={() => setDocumentType('DecPage')}
                >
                  Dec Page
                </button>
                <button
                  type="button"
                  className={`cs-tab${documentType === 'QuoteLetter' ? ' cs-tab--active' : ''}`}
                  onClick={() => setDocumentType('QuoteLetter')}
                >
                  Quote Letter
                </button>
              </div>
              <div
                className={`cs-dropzone aq-dropzone${scanDragging ? ' cs-dropzone--dragging' : ''}`}
                onDrop={onScanDrop}
                onDragOver={(e) => { e.preventDefault(); setScanDragging(true); }}
                onDragLeave={() => setScanDragging(false)}
              >
                {!scanFile ? (
                  <>
                    <div className="cs-dropzone__icon"><FiFileText size={22} /></div>
                    <p className="cs-dropzone__title">
                      Drag &amp; drop {documentType === 'DecPage' ? 'a dec page' : 'a quote letter'} PDF here
                    </p>
                    <p className="cs-dropzone__or">or</p>
                    <button type="button" className="cs-browse-btn cs-browse-btn--sm" onClick={() => scanInputRef.current?.click()}>
                      Browse Files
                    </button>
                    <input ref={scanInputRef} type="file" accept=".pdf" style={{ display: 'none' }} onChange={onScanFileInput} />
                  </>
                ) : (
                  <div className="cs-file-card">
                    <div className="cs-file-card__icon"><FiFileText size={18} /></div>
                    <div className="cs-file-card__info">
                      <span className="cs-file-card__name">{scanFile.name}</span>
                      <span className="cs-file-card__size">{(scanFile.size / 1024).toFixed(0)} KB</span>
                    </div>
                    <button type="button" className="cs-file-card__delete" onClick={() => setScanFile(null)} title="Remove">
                      <FiTrash2 size={14} />
                    </button>
                  </div>
                )}
              </div>
              {scanError && <p className="aq-error">{scanError}</p>}
              <button
                type="button"
                className="cs-browse-btn cs-browse-btn--sm aq-scan-btn"
                disabled={!scanFile || scanLoading}
                onClick={onScanAndPrefill}
              >
                {scanLoading ? 'Scanning…' : 'Scan & prefill'}
              </button>
            </section>

            <section className="sc-panel__section aq-section">
              <h3 className="sc-panel__section-title">Quote details</h3>
              <div className="aq-grid">
                <div className="aq-field">
                  <label className="aq-label">Quote number *</label>
                  <input className="glass-input" value={quoteNumber} onChange={(e) => setQuoteNumber(e.target.value)} placeholder="e.g. SUB-882301"/>
                </div>
                <div className="aq-field">
                  <label className="aq-label">Effective date *</label>
                  <input type="date" className="glass-input" value={quoteEffectiveDate} onChange={(e) => handleQuoteEffectiveDateChange(e.target.value)}/>
                </div>
                <div className="aq-field">
                  <label className="aq-label">Expiration date</label>
                  <input type="date" className="glass-input" value={quoteExpirationDate} onChange={(e) => setQuoteExpirationDate(e.target.value)}/>
                </div>
                <div className="aq-field">
                  <label className="aq-label">Carrier name *</label>
                  <input className="glass-input" value={quoteCarrierName} onChange={(e) => setQuoteCarrierName(e.target.value)} placeholder="e.g. Travelers"/>
                </div>
                <div className="aq-field">
                  <label className="aq-label">Business type</label>
                  <AqSelect value={quoteBusinessType} onChange={setQuoteBusinessType} options={Object.keys(BUSINESS_TYPE_VALUES)} />
                </div>
                <div className="aq-field">
                  <label className="aq-label">Premium *</label>
                  <input type="number" min="0" className="glass-input" value={quotePremium} onChange={(e) => setQuotePremium(e.target.value)} placeholder="e.g. 171450"/>
                </div>
              </div>
            </section>

            <section className="sc-panel__section aq-section">
              <h3 className="sc-panel__section-title">Note</h3>
              <textarea
                className="glass-input aq-note"
                rows={3}
                value={quoteNote}
                onChange={(e) => setQuoteNote(e.target.value)}
                placeholder="Add a note about this quote…"
              />
            </section>

            <section className="sc-panel__section aq-section">
              <h3 className="sc-panel__section-title">Documents</h3>
              {quoteDocs.length === 0 ? (
                <p className="sc-panel__note">No documents attached yet.</p>
              ) : (
                <ul className="sc-panel__list">
                  {quoteDocs.map((d, i) => (
                    <li key={`${d.name}-${i}`}>
                      <span className="sc-panel__list-icon"><FiCheck size={12} /></span>
                      <span className="sc-panel__list-main">
                        {d.name}
                        <span className="sc-panel__list-meta">{d.kind} · {d.note}</span>
                      </span>
                      <button type="button" className="aq-doc-remove" onClick={() => removeQuoteDoc(i)} aria-label={`Remove ${d.name}`}>
                        <FiTrash2 size={13} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <div
                className={`aq-attach${attachDragging ? ' aq-attach--dragging' : ''}`}
                onDrop={onAttachDrop}
                onDragOver={(e) => { e.preventDefault(); setAttachDragging(true); }}
                onDragLeave={() => setAttachDragging(false)}
              >
                <button type="button" className="cs-browse-btn cs-browse-btn--sm" onClick={() => attachInputRef.current?.click()}>
                  <FiPaperclip size={13} /> Attach documents
                </button>
                <span className="aq-attach__hint">or drag files here — attaches without scanning. You can attach as many as you need.</span>
                <input ref={attachInputRef} type="file" multiple style={{ display: 'none' }} onChange={onAttachFileInput} />
              </div>
            </section>

            {createQuoteError && <p className="aq-note" style={{ color: '#f87171' }}>{createQuoteError}</p>}

            <div className="aq-submitbar">
              {/* No "Back" here on purpose — the opportunity was already created by the
                  time this step is reached, so going back and hitting Continue again on
                  Step 2 would create a duplicate opportunity. */}
              <button type="button" className="glass-btn" disabled={creatingQuote} onClick={() => finish(false)}>Skip — create without a quote</button>
              <button type="button" className="cs-browse-btn" disabled={!quoteValid || creatingQuote} onClick={() => finish(true)}>
                {creatingQuote ? 'Creating…' : 'Add this quote & finish'}
              </button>
            </div>
          </>)}
      </div>
    </div>);
};
export default AddOpportunityPage;
